import React, { useState, useEffect, useMemo } from 'react';
import { EditNote } from '@mui/icons-material';
import { Grid } from '@mui/material';
import styled from 'styled-components';
import { IconButton, TagsList, type Tag } from '../../../../../../components';
import { CreateTagDialog } from '../../GSManagement/CreateTagDialog';
import { PANEL_TAG_TEXT } from '../../../../lang';

export type TagsFieldEditProps = {
  selectedTags: string[];
  allTags: string[];
  editable: boolean;
  handleUpdateTags: (tags: string[]) => void;
  handleCreateTag: (tagName: string) => void;
};

export const TagsFieldEdit: React.FunctionComponent<TagsFieldEditProps> = (
  props: TagsFieldEditProps,
) => {
  const { selectedTags, allTags, editable, handleUpdateTags, handleCreateTag } =
    props;

  const [conditions, setConditions] = useState<string[]>([]);
  const [openCreateTagDialog, setOpenCreateTagDialog] = useState(false);
  const tagList: Tag[] = useMemo(() => {
    return allTags.map(tag => {
      return { name: tag, selected: Boolean(conditions.includes(tag)) };
    });
  }, [conditions, allTags]);

  useEffect(() => {
    setConditions(selectedTags);
  }, [selectedTags]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'pop-over' : undefined;

  const handleOpenTagList = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseTagList = (): void => {
    setAnchorEl(null);
    handleUpdateTags(conditions);
  };

  const handleSelectTag = (tag: Tag): void => {
    if (conditions.includes(tag.name)) {
      setConditions(conditions.filter(condition => condition !== tag.name));
    } else {
      setConditions([...conditions, tag.name]);
    }
  };

  const handleOpenCreateTagDialog = (): void => {
    setOpenCreateTagDialog(true);
  };

  const handleCloseTagCreateDialog = (): void => {
    setOpenCreateTagDialog(false);
  };

  return (
    <Container>
      <Grid container spacing={1}>
        {conditions.map((condition, index) => {
          return (
            <Grid key={index}>
              <div className="tag-item">{condition}</div>
            </Grid>
          );
        })}
      </Grid>
      {editable && (
        <>
          <IconButton onClick={handleOpenTagList} icon={<EditNote />} />
          <TagsList
            id={id}
            listTitle="Tags"
            tags={tagList}
            open={open}
            anchorEl={anchorEl}
            buttonText={PANEL_TAG_TEXT.CREATE_NEW_PANEL_TAG}
            onClose={handleCloseTagList}
            onTagSelect={handleSelectTag}
            onCreateTag={handleOpenCreateTagDialog}
          />
        </>
      )}
      <CreateTagDialog
        open={openCreateTagDialog}
        title={PANEL_TAG_TEXT.CREATE_NEW_PANEL_TAG}
        helpText={PANEL_TAG_TEXT.SET_TAGS}
        onClose={handleCloseTagCreateDialog}
        onCreate={handleCreateTag}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  .tag-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 5px 8px;
    border-radius: 10px;
    background-color: #951d1d;
    color: #fff;
    margin-right: 5px;
    cursor: pointer;
  }
`;
